// RAT-1854: invoice language rules. An invoice may only be generated or sent in
// English or in the local language of the region it belongs to. The local
// language is configured per region under Regional Settings, so nothing here
// maps a country to a language - that mapping comes from the API.
//
// The names below only translate a language code the API returns into a label to
// show. The selectable set itself is served by the backend
// (GET region/invoice-languages) and used by the Regional Settings pages.

export const ENGLISH_LANGUAGE_ID = 'en';

// Sent to the backend by admin bulk generation: resolve each merchant's own
// local language at generation time, so one mixed-country batch produces a Dutch
// invoice for a Dutch merchant and a Spanish one for a Spanish merchant.
export const LOCAL_LANGUAGE_ID = 'local';

const LANGUAGE_LABELS = {
  en: 'English',
  nl: 'Dutch',
  es: 'Spanish',
};

export const languageLabel = (languageId) =>
  LANGUAGE_LABELS[languageId] ?? String(languageId ?? '').toUpperCase();

const englishOption = {
  id: ENGLISH_LANGUAGE_ID,
  label: LANGUAGE_LABELS[ENGLISH_LANGUAGE_ID],
};

/**
 * Options for every flow where the country is known: a single merchant, and a
 * dealer working inside their own region. English plus that region's local
 * language, each named explicitly. A region whose local language is English (or
 * that has none configured) therefore offers English only, never a duplicate.
 *
 * `submitLocal` makes the local entry keep its language name while submitting the
 * resolve-per-merchant value - used for dealer bulk, where the batch is resolved
 * merchant by merchant even though every merchant sits in the dealer's region.
 */
export const regionLanguageOptions = (regionLanguage, { submitLocal } = {}) => {
  if (!regionLanguage || regionLanguage === ENGLISH_LANGUAGE_ID) {
    return [englishOption];
  }
  return [
    englishOption,
    {
      id: submitLocal ? LOCAL_LANGUAGE_ID : regionLanguage,
      label: languageLabel(regionLanguage),
    },
  ];
};

/**
 * Admin bulk generation is the only flow whose batch can span countries, so it
 * offers exactly English and a generic "Local language" - never per-country
 * entries, whichever merchants are selected.
 */
export const bulkLanguageOptions = () => [
  englishOption,
  { id: LOCAL_LANGUAGE_ID, label: 'Local language' },
];

/** The option a form should start on: the local language when there is one. */
export const defaultLanguageOption = (options) =>
  options[options.length - 1] ?? englishOption;

/**
 * Options for re-downloading an invoice that already exists: English and the
 * local language of the invoice's own region, plus the language it was issued in
 * so an older invoice can always be re-downloaded exactly as it was sent.
 */
export const storedInvoiceLanguageOptions = (
  regionLanguage,
  storedLanguage,
) => {
  const options = regionLanguageOptions(regionLanguage);
  if (storedLanguage && !options.some((opt) => opt.id === storedLanguage)) {
    options.push({ id: storedLanguage, label: languageLabel(storedLanguage) });
  }
  return options;
};
